import React, { useState, useEffect, useRef, useMemo } from 'react';
import Benchmarks from '@/components/Pages/KidsHub/KidsTable/PastPerformanceChart/Benchmarks';
import XAxisItem, {
  XValue,
} from '@/components/Pages/KidsHub/KidsTable/PastPerformanceChart/XAxisItem';
import YAxisValues from '@/components/Pages/KidsHub/KidsTable/PastPerformanceChart/YAxisValues';
import styles from './PastPerformanceChart.module.scss';
import { useI18n } from 'next-localization';

interface Tooltip {
  year: number;
  index?: number;
}

interface ChartInfo {
  columnNames: string[];
}

interface Column {
  value: number | null;
  relativeValue?: number;
  isReversed?: boolean;
}

interface Entry {
  year: number;
  columns: Column[];
  tooltips: Tooltip[];
}

interface ChartData {
  entries: Entry[];
  chartInfo: ChartInfo;
}

interface PastPerformanceChartProps {
  chartData: ChartData;
}

const PastPerformanceChart: React.FC<PastPerformanceChartProps> = ({ chartData }) => {
  const { t } = useI18n();
  const [waterlineOffset, setWaterlineOffset] = useState<string>('0%');
  const [hasNegativeColumn, setHasNegativeColumn] = useState<boolean>(false);
  const xAxisRef = useRef<HTMLDivElement>(null);
  const chartWrapperRef = useRef<HTMLDivElement>(null);
  const benchmarksRef = useRef<HTMLDivElement>(null);

  const hasData = useMemo(
    () => chartData.entries.some((entry) => entry.columns.length > 0),
    [chartData.entries]
  );

  const benchmarks = useMemo(() => {
    const maxColumns = chartData.chartInfo.columnNames.length;
    return Array.from({ length: maxColumns }, (_, i) => ({
      values: chartData.entries.map((entry) => entry.columns[i]?.value || null),
    }));
  }, [chartData]);

  const maxAndMinEntryValue = useMemo(() => {
    const values = chartData.entries
      .flatMap((entry) => entry.columns.map((column) => column.value))
      .filter((value): value is number => value !== null);

    const max = Math.max(...values, 16);
    const min = Math.min(...values, 0);
    return { max, min };
  }, [chartData]);

  const yValues = useMemo(() => {
    const step = maxAndMinEntryValue.max > 20 || maxAndMinEntryValue.min < -20 ? 5 : 2;

    const generateValues = (step: number, startValue: number, targetValue: number) => {
      const steps = [{ value: startValue, visible: true }];
      let counter = startValue;

      if (startValue >= 0) {
        while (counter < targetValue) {
          const res = {
            value: counter + step,
            visible: counter + step <= maxAndMinEntryValue.max,
          };
          steps.push(res);
          counter += step;
        }
      } else {
        while (counter > targetValue) {
          const res = {
            value: counter - step,
            visible: counter - step >= maxAndMinEntryValue.min,
          };
          steps.push(res);
          counter -= step;
        }
      }

      return steps;
    };

    return {
      positive: {
        value: generateValues(step, step, Math.ceil(maxAndMinEntryValue.max)).reverse(),
        step,
      },
      negative: {
        value:
          maxAndMinEntryValue.min >= 0
            ? []
            : generateValues(step, -step, Math.floor(maxAndMinEntryValue.min)),
        step,
      },
    };
  }, [maxAndMinEntryValue]);

  const xValues = useMemo(() => {
    return chartData.entries.map((entry) => {
      entry.columns.forEach((column, idx) => {
        if (column.value === 0 || column.value === null) {
          column.relativeValue = 0;
        } else {
          const isNegative = column.value < 0;
          const yStep = isNegative ? yValues.negative.step : yValues.positive.step;
          column.relativeValue = Number(
            (
              (column.value /
                yStep /
                (yValues.positive.value.length + yValues.negative.value.length)) *
              100
            ).toFixed(2)
          );

          if (isNegative) {
            column.relativeValue = Math.abs(column.relativeValue);
            column.isReversed = true;
            setHasNegativeColumn(true);
          }
        }

        if (idx === 0 && column.value && column.value >= 0) {
          entry.tooltips.reverse();
        }
      });

      return entry;
    });
  }, [chartData.entries, yValues]);

  useEffect(() => {
    const negativeCount = yValues.negative.value.length;
    const totalCount = yValues.positive.value.length + negativeCount;
    setWaterlineOffset(((negativeCount * 100) / totalCount).toFixed(2) + '%');
  }, [yValues]);

  const initializeSyncedScroll = () => {
    if (xAxisRef.current && benchmarksRef.current) {
      const benchmarksElement = benchmarksRef.current;
      xAxisRef.current.addEventListener('scroll', (event) => {
        const scrollLeft = (event.target as HTMLElement).scrollLeft;
        benchmarksElement.scrollLeft = scrollLeft;
      });
    }
  };

  useEffect(() => {
    initializeSyncedScroll();
  }, []);

  return (
    <div ref={chartWrapperRef} className={styles['past-performance-wrapper']}>
      <div className={styles['past-performance-chart']}>
        {/* Y-Axis */}
        <div className={`${styles['y-axis']} ${styles.axis}`}>
          <div className={`${styles['y-axis__heading']} body-small`}>
            {t('pastPerformanceReturnsAxis')}
          </div>
          <YAxisValues yValues={yValues} />
        </div>

        {/* X-Axis */}
        {xValues.length > 0 && (
          <div ref={xAxisRef} className={styles['x-axis']}>
            {xValues.map((xValue) => (
              <XAxisItem
                key={xValue.year}
                xValue={xValue as XValue}
                columnBottomOffset={waterlineOffset}
                hasNegativeColumn={hasNegativeColumn}
              />
            ))}
          </div>
        )}

        {/* Waterline */}
        {hasNegativeColumn && (
          <div className={styles['waterline-container']}>
            <span className={styles.waterline} style={{ bottom: waterlineOffset }} />
          </div>
        )}

        {/* No Data Notice */}
        {!hasData && (
          <span className={`${styles['no-data-notice']} body-small`}>
            {t('pastPerformanceNoResultsMessage')}
          </span>
        )}
      </div>

      {/* Benchmarks */}
      {benchmarks.length > 0 && (
        <Benchmarks
          ref={benchmarksRef}
          className="mt-3 mt-xl-4 overflow-hidden"
          benchmarks={benchmarks}
        />
      )}
    </div>
  );
};

export default PastPerformanceChart;
